/**
 * Decisiones PURAS de la superficie de despacho manual (Fase B).
 *
 * Vive fuera de `app/actions/deliveryActions.ts` por la misma razón que
 * `dispatchGuards.ts`: en un archivo `"use server"` todo export debe ser una
 * función async, así que constantes y helpers síncronos romperían `next build`.
 * Además el cliente (la tarjeta de "Órdenes listas para enviar") necesita las
 * MISMAS reglas que el servidor — tenerlas en un módulo puro evita que las dos
 * puntas se contradigan.
 */
import type { DeliveryQuote } from "./provider"

/**
 * Estado de orden desde el que se puede despachar un courier.
 *
 * `ready` = la cocina terminó. Es el acople natural que ya identificaba §5.6 del
 * diseño: el courier llega cuando hay comida, no cuando entra la orden. Antes de
 * `ready` el courier esperaría en el local (y Uber cobra igual); después de
 * `completed` la orden ya salió por otro canal.
 */
export const DISPATCHABLE_ORDER_STATUS = "ready"

/**
 * Terminales de FALLO de un envío. Misma lista que usa la vista de "Entregas de
 * hoy" para decidir si ofrece re-despachar — se declara UNA vez acá y ambas la
 * importan, porque si las dos superficies discrepan una de ellas termina
 * ofreciendo despachar sobre un courier vivo.
 */
export const DELIVERY_FAILURE_TERMINAL: ReadonlySet<string> = new Set([
  "canceled",
  "failed",
  "returned",
])

/**
 * Estados de ORDEN cerrada: la comida ya salió por otro camino (el dueño la
 * llevó, se entregó) o la orden se anuló/reembolsó. Mismo conjunto con el que el
 * despacho automático decide no mandar courier (`autoDispatch.ts`).
 */
export const ORDER_CLOSED_STATUSES: ReadonlySet<string> = new Set([
  "completed",
  "voided",
  "cancelled",
  "canceled",
  "refunded",
])

/**
 * Ventana de órdenes candidatas. Las órdenes `ready` NO se archivan solas (ver
 * `lib/kds/legacyOrders.ts`), así que sin ventana la lista acumularía órdenes de
 * hace semanas que nadie va a enviar.
 */
export const DISPATCHABLE_WINDOW_HOURS = 12

/** Tope de filas de la lista. Mismo orden de magnitud que el panel de listas del KDS. */
export const DISPATCHABLE_ORDERS_LIMIT = 60

/**
 * Margen de seguridad al validar la cotización DEL LADO DEL CLIENTE.
 *
 * `assertFreshQuote` (servidor) es el árbitro real y no perdona: vencimiento
 * exactamente ahora ya cuenta como vencido. Entre que el navegador decide
 * despachar y el servidor re-valida hay un viaje de red; sin margen, una
 * cotización con 200 ms de vida se manda igual y rebota con `QuoteExpiredError`.
 * Con el margen la UI se adelanta y re-cotiza sola, que es lo que el dueño
 * espera ver.
 */
export const QUOTE_CLIENT_SAFETY_MARGIN_MS = 5_000

/**
 * Margen para decidir si el cliente ALCANZA A PAGAR con esta cotización: escribir
 * nombre, teléfono y tarjeta toma ~2 min. Es una pregunta distinta a la de
 * `QUOTE_CLIENT_SAFETY_MARGIN_MS` (5 s: "¿puedo mostrar este precio?"), que sigue
 * siendo el default del helper. Este margen lo pasan EXPLÍCITAMENTE la barra de
 * modo, el salto del paso de dirección y la re-cotización silenciosa del checkout
 * (spec 2026-09-03 §4.2). Sin él, una cotización a 40 s de vencer saltaría el paso y
 * el cliente comería el 409 después de escribir la tarjeta.
 */
export const QUOTE_CHECKOUT_MARGIN_MS = 120_000

export interface DeliveryStatusRef {
  order_id: string
  status: string
}

export interface OrderCandidate {
  id: string
  status?: string | null
}

/**
 * ¿Se puede despachar un courier contra esta orden?
 *
 * Dos condiciones, ambas necesarias:
 *  1. La orden está en `ready`.
 *  2. NINGÚN envío suyo sigue vivo. "Vivo" es todo lo que no sea un terminal de
 *     fallo — incluido `created`, que es justamente el estado de resultado
 *     DESCONOCIDO (`dispatchDelivery` lo deja ahí cuando no puede probar que
 *     Uber rechazó). Tratar `created` como "no pasó nada" y volver a despachar
 *     es exactamente cómo se duplican couriers y se le cobra dos veces al
 *     cliente.
 *
 * Una orden cuyos envíos están TODOS en terminal de fallo sí vuelve a ser
 * despachable: no hay courier detrás y el intento anterior está cerrado.
 */
export function isOrderDispatchable(
  order: OrderCandidate,
  deliveryStatuses: readonly string[],
): boolean {
  if (order.status !== DISPATCHABLE_ORDER_STATUS) return false
  return deliveryStatuses.every((s) => DELIVERY_FAILURE_TERMINAL.has(s))
}

/**
 * Filtra las órdenes candidatas contra las filas de `deliveries` que existan
 * para ellas. Devuelve las despachables conservando el orden de entrada.
 */
export function filterDispatchableOrders<T extends OrderCandidate>(
  orders: readonly T[],
  deliveries: readonly DeliveryStatusRef[],
): T[] {
  const byOrder = new Map<string, string[]>()
  for (const d of deliveries) {
    const list = byOrder.get(d.order_id)
    if (list) list.push(d.status)
    else byOrder.set(d.order_id, [d.status])
  }
  return orders.filter((o) => isOrderDispatchable(o, byOrder.get(o.id) ?? []))
}

/**
 * Espejo CLIENTE de `assertFreshQuote`. No lo reemplaza: el servidor sigue
 * siendo el árbitro y el que puede negarse. Esto solo existe para que la UI
 * nunca muestre un precio que ya no se puede cobrar, y para re-cotizar antes de
 * mandar una tarifa muerta.
 *
 * Misma frontera que el guard del servidor: `expiresAt` no parseable cuenta como
 * VENCIDO (no se puede demostrar que siga viva), igual que el vencimiento exacto.
 */
export function isQuoteExpired(
  quote: Pick<DeliveryQuote, "expiresAt">,
  now: number = Date.now(),
  marginMs: number = QUOTE_CLIENT_SAFETY_MARGIN_MS,
): boolean {
  const expiresAt = Date.parse(quote.expiresAt)
  if (Number.isNaN(expiresAt)) return true
  return expiresAt <= now + marginMs
}

/** Segundos que le quedan de vida a la cotización (0 si ya venció). Para el contador. */
export function quoteSecondsLeft(
  quote: Pick<DeliveryQuote, "expiresAt">,
  now: number = Date.now(),
): number {
  const expiresAt = Date.parse(quote.expiresAt)
  if (Number.isNaN(expiresAt)) return 0
  return Math.max(0, Math.floor((expiresAt - now) / 1000))
}
